import React, { useState, useEffect } from 'react';
import { PageShell } from '../../components/Ui';
import { useAuth } from '../../auth/AuthContext';
import { ipApi } from '../../services/api';
import { MdCheckCircle, MdCancel } from 'react-icons/md';

const TODAY = new Date().toISOString().slice(0, 10);
const LEAVE_TYPES = ['Casual Leave', 'Sick Leave', 'Earned Leave', 'Maternity/Paternity', 'Unpaid Leave', 'Emergency'];

function StatusChip({ status }) {
  const map = {
    Pending: { bg: '#fefce8', color: '#ca8a04' },
    Approved: { bg: '#f0fdf4', color: '#16a34a' },
    Rejected: { bg: '#fef2f2', color: '#dc2626' },
  };
  const c = map[status] || map.Pending;
  return (
    <span style={{ background: c.bg, color: c.color, borderRadius: 999, padding: '2px 10px', fontSize: 11.5, fontWeight: 700 }}>
      {status}
    </span>
  );
}

export default function StaffLeavePage() {
  const { user } = useAuth();
  const isHr = user?.role === 'manager' || user?.role === 'hr';
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: 'Casual Leave', from: TODAY, to: TODAY, reason: '' });
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      const data = await ipApi.getHrLeave(80);
      setLeaves(data.leaves || []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    try {
      await ipApi.createHrLeave({
        staffName: user?.name,
        leaveType: form.type,
        fromDate: form.from,
        toDate: form.to,
        reason: form.reason,
      });
      setMsg('Leave application submitted for HR approval.');
      setShowForm(false);
      setForm({ type: 'Casual Leave', from: TODAY, to: TODAY, reason: '' });
      await reload();
    } catch (ex) {
      setErr(ex.message);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await ipApi.patchHrLeave(id, { status });
      await reload();
    } catch (ex) {
      setErr(ex.message);
    }
  };

  return (
    <PageShell
      title={isHr ? 'Leave Management' : 'My Leave'}
      subtitle={isHr ? 'Approve team leave requests' : 'Apply for leave and track status'}
      actions={(
        <button type="button" className="btn btn-primary" onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Cancel' : '+ Apply Leave'}
        </button>
      )}
    >
      {msg && <div className="info-banner mb">{msg}</div>}
      {err && <div className="error mb">{err}</div>}

      {showForm && (
        <form className="card mb form-grid" onSubmit={submit}>
          <h3 style={{ gridColumn: '1/-1', marginTop: 0 }}>Leave application — {user?.name}</h3>
          <div className="field">
            <label>Leave type</label>
            <select className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {LEAVE_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="field"><label>From</label><input type="date" className="input" required value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} /></div>
          <div className="field"><label>To</label><input type="date" className="input" required value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} /></div>
          <div className="field full"><label>Reason</label><input className="input" required value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Brief reason" /></div>
          <div className="form-actions full"><button type="submit" className="btn btn-primary">Submit</button></div>
        </form>
      )}

      <div className="card">
        <h3 style={{ marginTop: 0 }}>{isHr ? 'All leave requests' : 'My leave history'}</h3>
        {loading && <p className="hint">Loading…</p>}
        {leaves.map((l) => (
          <div key={l.leaveId} className="leave-card" style={{ marginBottom: 10 }}>
            <div className="leave-card-head">
              <div>
                <div className="leave-card-name">{l.staffName}</div>
                <div className="leave-card-meta">{l.leaveType} · {l.fromDate} to {l.toDate}</div>
                {l.reason && <div className="leave-card-reason">"{l.reason}"</div>}
              </div>
              <StatusChip status={l.status} />
            </div>
            {isHr && l.status === 'Pending' && (
              <div className="leave-actions">
                <button type="button" className="btn btn-sm" style={{ background: '#16a34a', color: '#fff' }} onClick={() => updateStatus(l.leaveId, 'Approved')}>
                  <MdCheckCircle /> Approve
                </button>
                <button type="button" className="btn btn-sm" style={{ background: '#dc2626', color: '#fff' }} onClick={() => updateStatus(l.leaveId, 'Rejected')}>
                  <MdCancel /> Reject
                </button>
              </div>
            )}
          </div>
        ))}
        {!loading && leaves.length === 0 && <p className="hint table-empty">No leave applications yet.</p>}
      </div>
    </PageShell>
  );
}
